/**
 * Markdown Front Matter Parser Utility
 *
 * This utility provides functions to parse YAML front matter from markdown files
 * and build navigation structures from the TOC.md file.
 */

/**
 * Parse YAML front matter from markdown content
 * @param {string} markdownContent - Raw markdown content with front matter
 * @returns {Object} - { frontMatter: Object, content: string }
 */
export function parseFrontMatter(markdownContent) {
  const frontMatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = markdownContent.match(frontMatterRegex);

  if (!match) {
    return {
      frontMatter: {},
      content: markdownContent
    };
  }

  const yamlContent = match[1];
  const content = match[2];

  try {
    const frontMatter = parseYAML(yamlContent);
    return { frontMatter, content };
  } catch (error) {
    console.warn('Failed to parse front matter:', error);
    return {
      frontMatter: {},
      content: markdownContent
    };
  }
}

/**
 * Simple YAML parser for front matter (supports basic key-value pairs and arrays)
 * @param {string} yamlContent - YAML content to parse
 * @returns {Object} - Parsed YAML object
 */
function parseYAML(yamlContent) {
  const result = {};
  const lines = yamlContent.split('\n');

  for (let line of lines) {
    line = line.trim();
    if (!line || line.startsWith('#')) continue;

    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) continue;

    const key = line.substring(0, colonIndex).trim();
    let value = line.substring(colonIndex + 1).trim();

    // Handle quoted strings
    if ((value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }
    // Handle arrays
    else if (value.startsWith('[') && value.endsWith(']')) {
      value = value.slice(1, -1).split(',').map(item =>
        item.trim().replace(/^["']|["']$/g, '')
      );
    }
    // Handle booleans
    else if (value === 'true') {
      value = true;
    } else if (value === 'false') {
      value = false;
    }
    // Handle numbers
    else if (!isNaN(value) && value !== '') {
      value = Number(value);
    }

    result[key] = value;
  }

  return result;
}

/**
 * Parse TOC.md content to build navigation structure
 * @param {string} tocContent - Content of TOC.md file
 * @returns {Array} - Navigation structure array
 */
export function parseTOC(tocContent) {
  const { content } = parseFrontMatter(tocContent);
  const lines = content.split('\n');
  const navigation = [];
  let stack = [];
  let currentCategory = null;

  for (const line of lines) {
    const trimmed = line.trim();

    // Handle category headers
    if (trimmed.startsWith('###')) {
      currentCategory = {
        title: trimmed.replace('###', '').trim(),
        items: []
      };
      navigation.push(currentCategory);
      stack = []; // Reset stack for new category
      continue;
    }

    // Skip empty lines and non-list items
    if (!trimmed || !trimmed.startsWith('-')) continue;

    // Determine nesting level (count spaces, divide by 4 for 4-space indentation)
    const indentMatch = line.match(/^(\s*)/);
    const indentSpaces = indentMatch ? indentMatch[1].length : 0;
    const level = Math.floor(indentSpaces / 4); // Use 4 spaces per level instead of 2

    // Parse markdown link
    const linkMatch = trimmed.match(/^-\s*\[([^\]]+)\]\(([^)]+)\)(?:\s*-\s*(.+))?/);
    if (!linkMatch) {
      continue;
    }

    const [, title, url, description] = linkMatch;

    const item = {
      title: title.trim(),
      url: url.trim(),
      slug: url.trim().replace(/\.md$/, ''),
      description: description ? description.trim() : '',
      children: []
    };

    // Adjust stack to current level
    while (stack.length > level) {
      stack.pop();
    }

    // Add to appropriate parent
    if (level === 0) {
      // Top-level item - add to current category or navigation root
      if (currentCategory) {
        currentCategory.items.push(item);
      } else {
        navigation.push(item);
      }
    } else {
      // Nested item - add to parent's children
      if (stack.length > 0) {
        stack[stack.length - 1].children.push(item);
      } else {
        // Fallback: if no parent in stack, add to category or root
        if (currentCategory) {
          currentCategory.items.push(item);
        } else {
          navigation.push(item);
        }
      }
    }

    stack.push(item);
  }

  return navigation;
}

/**
 * Build breadcrumb navigation for a given page
 * @param {Array} navigation - Navigation structure from parseTOC
 * @param {string} currentSlug - Current page slug
 * @returns {Array} - Breadcrumb array
 */
export function buildBreadcrumbs(navigation, currentSlug) {
  function findPath(items, slug, path = []) {
    for (const item of items) {
      // Skip category headers
      if (item.items) {
        const categoryPath = findPath(item.items, slug, path);
        if (categoryPath) return categoryPath;
        continue;
      }

      const itemSlug = item.slug || item.url.replace('.md', '');
      const newPath = [...path, item];

      if (itemSlug === slug) {
        return newPath;
      }

      if (item.children && item.children.length > 0) {
        const childPath = findPath(item.children, slug, newPath);
        if (childPath) return childPath;
      }
    }
    return null;
  }

  return findPath(navigation, currentSlug) || [];
}

/**
 * Flatten navigation structure for easier lookup
 * @param {Array} navigation - Navigation structure
 * @returns {Map} - Map of slug to item
 */
export function flattenNavigation(navigation) {
  const flatMap = new Map();

  function processItems(items, category = null) {
    for (const item of items) {
      // Handle category headers
      if (item.items) {
        processItems(item.items, item.title);
        continue;
      }

      const slug = item.slug || item.url.replace('.md', '');
      flatMap.set(slug, { ...item, category });

      if (item.children && item.children.length > 0) {
        processItems(item.children, category);
      }
    }
  }

  processItems(navigation);
  return flatMap;
}
